<template>
  <TheMainContent v-if="orderline" class="contents">
    <FailLoadingScreen
      v-if="displayFailLoadingScreen"
      data-qa="resolution-flow-fail-loading-screen"
      data-test="resolution-flow-fail-loading-screen"
      @retry="onRetry"
    />

    <InvalidProblemErrorScreen
      v-else-if="displayInvalidLoadingProblemContextErrorScreen"
    />

    <LoadingScreen
      v-else-if="displayLoadingScreen"
      data-qa="resolution-flow-loading-screen"
      data-test="resolution-flow-loading-screen"
      :loading-text="i18n(translations.initialLoadingText)"
    />

    <ResolutionFlowScreens
      v-else
      :machine-send="send"
      :machine-state="state"
      :order-id="orderline.orderId"
      :product-image="orderline.product.image"
      :product-title="orderline.product.title"
      @cancel="onCancel"
    />

    <CancellationModal />
  </TheMainContent>
  <Intercom :manual="true" />
</template>

<script lang="ts" setup>
import {
  createError,
  navigateTo,
  useAsyncData,
  useRoute,
  useRouteLocationWithLocale,
} from '#imports'
import { computed, onMounted, onUnmounted, provide, shallowRef } from 'vue'
import type { RouteLocationRaw } from 'vue-router'

import Intercom from '@backmarket/nuxt-layer-care-platform/intercom/components/Intercom.vue'
import { useUserStore } from '@backmarket/nuxt-layer-oauth/useUserStore'
import { getAsyncDataKey } from '@backmarket/nuxt-module-http-v2/getAsyncDataKey'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { openModal } from '@ds/components/ModalBase'
import { interpret } from 'xstate'

import { useOrderlineLightDetails } from '@/scopes/care-commons/components/order/OrderlineCard/composables/useOrderlineLightDetails'
import { SALES_CUSTOMER_CARE_SCOPE } from '@/scopes/care-commons/utils/logger/salesCustomerCare/config'
import TheMainContent from '~/components/TheMainContent/TheMainContent.vue'
import { CUSTOMER_CARE_MODALS_NAMES } from '~/scopes/customer-care/common/modals/names'
import { ResolutionFlowStateMachineKey } from '~/scopes/customer-care/resolution/symbols'
import { CUSTOMER_CARE } from '~/scopes/customer-care/route-names'

import FailLoadingScreen from '../common/components/FailLoadingScreen/FailLoadingScreen.vue'
import InvalidProblemErrorScreen from '../common/components/InvalidProblemErrorScreen/InvalidProblemErrorScreen.vue'
import LoadingScreen from '../common/components/LoadingScreen/LoadingScreen.vue'
import CancellationModal from '../common/modals/CancellationModal/CancellationModal.vue'
import { decodeIssues } from '../common/utils/issueCommunication'

import translations from './ResolutionFlow.translations'
import ResolutionFlowScreens from './components/ResolutionFlowScreens/ResolutionFlowScreens.vue'
import { createResolutionFlowMachine } from './machine/ResolutionFlow.machine'

const i18n = useI18n()
const { params, matched, name } = useRoute()
const resolveLocalizedRoute = useRouteLocationWithLocale()
const { trackClick } = useTracking()

const { user } = useUserStore()

const { fetchOrderlineLightDetails } = useOrderlineLightDetails()
const getOrderlineLightDetailsKey = getAsyncDataKey(
  'resolution-flow-get-orderline-light-details',
  {
    orderlineId: params.orderlineId,
  },
)
const {
  execute,
  data: orderline,
  error,
} = await useAsyncData(
  getOrderlineLightDetailsKey,
  () =>
    fetchOrderlineLightDetails(
      params.orderlineId as string,
      SALES_CUSTOMER_CARE_SCOPE.resolutionFlow,
    ),
  { immediate: false },
)

await execute()

if (error.value) {
  throw createError({
    statusCode: error.value.statusCode,
    statusMessage: error.value.message,
    fatal: true,
  })
}

const decodedCustomerIssues = decodeIssues({
  issuesBase64String: params.customerIssues as string,
  scope: SALES_CUSTOMER_CARE_SCOPE.resolutionFlow,
})

const resolutionFlowMachine = createResolutionFlowMachine(
  'GET_DIAGNOSIS_ACTION',
)

const state = shallowRef(resolutionFlowMachine.initialState)

const service = interpret(
  resolutionFlowMachine.withContext({
    ...resolutionFlowMachine.context,
    clientId: user.value.clientId,
    diagnosisV2CustomerIssues: decodedCustomerIssues?.selectedIssues,
    diagnosisV2CustomerIssuesTags: decodedCustomerIssues?.tags,
    orderline: orderline.value ?? null,
    orderlineId: orderline.value?.orderlineId ?? null,
    publicId: orderline.value?.publicId ?? null,
  }),
  {
    devTools: false,
  },
).onTransition((newState) => {
  state.value = newState
})

const { send } = service

provide(ResolutionFlowStateMachineKey, {
  state,
  send,
})

onMounted(() => {
  service.start()
})

onUnmounted(() => {
  service.stop()
})

const displayFailLoadingScreen = computed(() =>
  state.value.hasTag('loading_failed'),
)

const displayInvalidLoadingProblemContextErrorScreen = computed(() =>
  state.value.hasTag('loading_problem_context_failed'),
)

const displayLoadingScreen = computed(() => state.value.hasTag('loading'))

function onRetry() {
  trackClick({
    zone: 'resolution_flow',
    page_type: name as string,
    name: 'resolution_flow_error',
    orderlineId: params.orderlineId,
  })
  send('RETRY')
}

function onCancel() {
  openModal(CUSTOMER_CARE_MODALS_NAMES.cancellationModal)
}
if (
  matched.some(
    ({ name: matchedRouteName }) =>
      matchedRouteName === CUSTOMER_CARE.RESOLUTION.EXPRESS_REPLACEMENT.SELF,
  ) &&
  name !== CUSTOMER_CARE.RESOLUTION.EXPRESS_REPLACEMENT.PAYMENT
) {
  navigateTo(
    resolveLocalizedRoute({
      name: CUSTOMER_CARE.RESOLUTION.SELF,
    }) as RouteLocationRaw,
    { replace: true },
  )
}
</script>
